"use client";

import { cn } from "@/lib/shared/utils";
import { useMemo } from "react";
import { useEditMode } from "@/contexts/EditModeContext";
import EditableText from "@/components/edit-mode/EditableText";

interface MarqueeProps {
  text: string | string[];
  className?: string;
  textClassName?: string;
  repeat?: number;
  reverse?: boolean;
  filled?: boolean;
}

export default function Marquee({
  text,
  className,
  textClassName,
  repeat = 10,
  reverse = false,
  filled,
}: MarqueeProps) {
  const { isEditing } = useEditMode();

  const textItems = useMemo(() => (Array.isArray(text) ? text : [text]), [text]);

  const marqueeItems = useMemo(() => {
    const items: Array<{ text: string; key: string; originalIndex: number }> = [];
    for (let i = 0; i < repeat; i++) {
      textItems.forEach((item, itemIndex) => {
        items.push({
          text: item,
          key: `${i}-${itemIndex}`,
          originalIndex: itemIndex,
        });
      });
    }
    return items;
  }, [textItems, repeat]);

  const editItems = useMemo(() => {
    return textItems.map((item, i) => ({
      text: item,
      key: `edit-${i}`,
      originalIndex: i,
    }));
  }, [textItems]);

  const section = textItems.length > 1 ? "marqueeTop" : "marqueeBottom";
  const field = "items.0";

  const renderItems = (items: Array<{ text: string; key: string; originalIndex: number }>) =>
    items.map((item) => {
      const isFilled = filled ?? true;
      const baseClasses = cn(
        "text-5xl font-black tracking-tighter uppercase transition-colors duration-300 md:text-7xl",
        isFilled ? "text-background" : "stroke-text text-transparent",
        textClassName
      );
      const strokeStyle: React.CSSProperties = !isFilled
        ? { WebkitTextStroke: "1px var(--background)" }
        : {};

      return (
        <span key={item.key} className={baseClasses} style={strokeStyle}>
          {isEditing ? (
            <EditableText
              section={section}
              field={section === "marqueeTop" ? `items.${item.originalIndex}` : field}
              value={item.text}
            />
          ) : (
            item.text
          )}
        </span>
      );
    });

  if (!isEditing) {
    return (
      <div
        className={cn(
          "border-foreground/10 bg-foreground relative flex touch-none overflow-hidden border-y py-6",
          className
        )}
      >
        <span className="sr-only">{textItems.join(" ")}</span>
        <div aria-hidden className="contents">
          <div
            className={cn(
              "animate-marquee flex items-center gap-12 whitespace-nowrap",
              reverse && "animate-marquee-reverse"
            )}
          >
            {renderItems(marqueeItems)}
          </div>
          <div
            className={cn(
              "animate-marquee2 absolute top-0 flex items-center gap-12 py-6 whitespace-nowrap",
              reverse && "animate-marquee2-reverse"
            )}
          >
            {renderItems(marqueeItems)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "border-foreground/10 bg-foreground relative flex overflow-hidden border-y py-6",
        className
      )}
    >
      <span className="sr-only">{textItems.join(" ")}</span>
      <div aria-hidden className="contents w-full">
        <div className="flex items-center gap-12 px-4 whitespace-nowrap">
          {renderItems(editItems)}
        </div>
      </div>
    </div>
  );
}
